<script setup lang="ts">
type DataRequestData = App.DataRequests.Data.DataRequestData;
type EnumData = App.Base.Data.EnumData;
type StudentScholarshipData = App.Students.Data.StudentScholarshipData;

import type { HybridlyFormData } from '@/@types/global';

import { useDataRequestIndicators } from '@/hooks/useDataRequestIndicators';

import Alert from '@/components/alert/Alert.vue';
import FormError from '@/components/formError/FormError.vue';
import FormInstituteInput from '@/components/formComboboxInput/FormInstituteInput.vue';
import FormListboxInput from '@/components/formListboxInput/FormListboxInput.vue';
import FormRadioInput from '@/components/formRadioInput/FormRadioInput.vue';
import FormRows from '@/components/formRows/FormRows.vue';
import FormScholarshipInput from '@/components/formComboboxInput/FormScholarshipInput.vue';
import FormSwitchInput from '@/components/formSwitchInput/FormSwitchInput.vue';
import FormTextareaInput from '@/components/formTextareaInput/FormTextareaInput.vue';
import FormTextInput from '@/components/formTextInput/FormTextInput.vue';

import TimesCircle from '@/icons/line/times-circle.svg';

const props = defineProps<{
  dataRequest?: DataRequestData;
  form: HybridlyFormData<{
    scholarship: {
      scholarship_id: number;
      type: EnumData;
      institute_id: number;
      status: EnumData;
      amount: number;
      can_renew: boolean;
      years_renewable: number;
    };
    user_comment: string;
  }>;
  types: EnumData[];
  scholarship: StudentScholarshipData;
  statuses: EnumData[];
}>();

const { computedStatusBadge, hasAttributeChange } = useDataRequestIndicators({
  dataRequest: props.dataRequest,
  base: props.scholarship
});

function handleCanRenewChange(value: boolean) {
  props.form.fields.scholarship.can_renew = value;
  props.form.fields.scholarship.years_renewable = value
    ? props.form.initial.scholarship.years_renewable
    : 1;
}

function handleTypeChange(type: string | boolean | null) {
  props.form.fields.scholarship.type = type;
  props.form.fields.scholarship.institute_id = null;
}
</script>

<template>
  <div class="-mx-5 -mb-8 -mt-6 flex flex-col divide-y divide-slate-200">
    <div class="px-5 pb-5 pt-6" v-if="dataRequest?.status.value === 'rejected'">
      <Alert
        v-if="dataRequest?.status.value === 'rejected'"
        :description="`Reason: ${dataRequest.rejected_reason ?? 'No reason provided'}`"
        :icon="TimesCircle"
        title="Submission was not approved"
        color="danger"
        variant="soft"
      />
    </div>
    <FormRows class="px-5 pb-8 pt-6">
      <FormScholarshipInput
        :error="form.errors.scholarship?.scholarship_name"
        :modelValue="form.fields.scholarship.scholarship_name"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="
          hasAttributeChange('scholarship.name', 'scholarship_name')
            ? computedStatusBadge.label
            : undefined
        "
        label="Scholarship"
        @update:modelValue="(value) => (form.fields.scholarship.scholarship_name = value)"
      />
      <FormRadioInput
        label="Scholarship type"
        :error="form.errors.scholarship?.type"
        :isFieldset="false"
        :radioInputs="types"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="hasAttributeChange('type') ? computedStatusBadge.label : undefined"
        :value="form.fields.scholarship.type"
        ariaLabel="Scholarship type"
        name="type"
        variant="outlined"
        @update:modelValue="handleTypeChange"
      />
      <FormInstituteInput
        v-if="form.fields.scholarship.type === 'institutional-aid'"
        :error="form.errors.scholarship?.institute_id"
        :modelValue="form.fields.scholarship.institute_id"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="hasAttributeChange('institute') ? computedStatusBadge.label : undefined"
        label="Institution"
        @update:modelValue="(value) => (form.fields.scholarship.institute_id = value)"
      />
      <FormListboxInput
        :error="form.errors.scholarship?.status"
        :modelValue="form.fields.scholarship.status"
        :options="statuses"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="hasAttributeChange('status') ? computedStatusBadge.label : undefined"
        label="Scholarship status"
        @update:modelValue="(value) => (form.fields.scholarship.status = value)"
      />
      <FormTextInput
        :error="form.errors.scholarship?.amount"
        :min="0"
        :modelValue="form.fields.scholarship.amount"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="hasAttributeChange('amount') ? computedStatusBadge.label : undefined"
        isOptional
        label="Amount"
        name="amount"
        placeholder="Enter amount..."
        prepend="$"
        type="number"
        @update:modelValue="(value) => (form.fields.scholarship.amount = value)"
      />
      <div class="flex flex-col gap-y-1.5">
        <FormSwitchInput
          :checked="form.fields.scholarship.can_renew"
          :statusBadgeColor="computedStatusBadge.color"
          :statusBadgeIcon="computedStatusBadge.icon"
          :statusBadgeLabel="
            hasAttributeChange('years_renewable') ? computedStatusBadge.label : undefined
          "
          class="pointer-events-auto"
          label="This scholarship can be renewed"
          helperText="Some scholarships may provide funding for multiple years if you meet specific criteria."
          name="can_renew"
          @update:checked="(value) => handleCanRenewChange(value)"
        />
        <FormError v-if="form.errors.scholarship?.years_renewable">{{
          form.errors.scholarship?.years_renewable
        }}</FormError>
      </div>
      <FormTextInput
        v-if="form.fields.scholarship.can_renew"
        :min="1"
        :modelValue="form.fields.scholarship.years_renewable"
        helperText="Total number of years the scholarship will be awarded (including first year)"
        label="Years renewable"
        name="years_renewable"
        placeholder="e.g. 2"
        type="number"
        @update:modelValue="(value) => (form.fields.scholarship.years_renewable = value)"
      />
    </FormRows>
    <FormRows class="px-5 pb-8 pt-6">
      <FormTextareaInput
        label="Note"
        helpText="Note to adviser about this submission"
        :error="form.errors.user_comment"
        :modelValue="form.fields.user_comment"
        name="user_comment"
        isOptional
        @update:modelValue="(value) => (form.fields.user_comment = value)"
      />
    </FormRows>
  </div>
</template>
